import client from './client';
import type { OnboardingStatus, CustomerProfile } from '../types/api';

export const onboardingApi = {
    /**
     * Get current user's onboarding status
     */
    getStatus: async (): Promise<OnboardingStatus> => {
        const response = await client.get('/api/v1/onboarding/status');
        return response.data;
    },

    /**
     * Update profile during onboarding
     */
    updateProfile: async (profile: Partial<CustomerProfile>): Promise<{ message: string }> => {
        const response = await client.post('/api/v1/onboarding/profile', profile);
        return response.data;
    },

    /**
     * Register signed document in the vault
     */
    signDocument: async (
        docType: 'BAA' | 'CompanyBAA' | 'HIPAA' | 'Agreement' | 'FinancialResponsibility' | 'UserAgreement' | 'StaffAgreement',
        customerId: string,
        staffId?: string
    ): Promise<{ message: string; path: string }> => {
        const response = await client.post('/api/v1/onboarding/sign', {
            doc_type: docType,
            customer_id: customerId,
            staff_id: staffId
        });
        return response.data;
    },

    /**
     * Activate staff account after signing docs
     */
    activateStaffContext: async (): Promise<{ message: string; onboarding_status: string }> => {
        const response = await client.post('/api/v1/onboarding/activate-staff');
        return response.data;
    },

    /**
     * Create a Stripe Checkout Session for plan subscription
     */
    createCheckoutSession: async (
        priceId: string,
        successUrl: string,
        cancelUrl: string
    ): Promise<{ checkout_url: string }> => {
        const response = await client.post('/api/v1/billing/checkout-session', {
            price_id: priceId,
            success_url: successUrl,
            cancel_url: cancelUrl
        });
        return response.data;
    },
    /**
     * Initiate a new organization enrollment
     */
    enroll: async (): Promise<{ customer_id: string; customer_staff_key: string }> => {
        const response = await client.post('/api/v1/onboarding/enroll');
        return response.data;
    },

    /**
     * Fetch dynamic subscription plans from Stripe
     */
    getPlans: async (): Promise<any[]> => {
        const response = await client.get('/api/v1/billing/plans');
        return response.data;
    },

    /**
     * Create a Stripe Checkout Session for bulk credit purchase
     */
    createBulkCheckout: async (
        successUrl: string,
        cancelUrl: string
    ): Promise<{ checkout_url: string }> => {
        const response = await client.post('/api/v1/billing/bulk-checkout', {
            success_url: successUrl,
            cancel_url: cancelUrl
        });
        return response.data;
    },
    /**
     * List all practices that have provisioned this staff email
     */
    listInvitations: async (): Promise<any[]> => {
        const response = await client.get('/api/v1/onboarding/invitations');
        return response.data;
    },

    /**
     * Choose an organization context for onboarding and redeem the provision key
     */
    redeemInvitation: async (customerStaffKey: string): Promise<{ message: string; status?: string }> => {
        const response = await client.post('/api/v1/onboarding/redeem', { customer_staff_key: customerStaffKey });
        return response.data;
    },

    /**
     * Activate account after confirmed Stripe subscription payment.
     * Can be called from SubscriptionSuccess page or the billing tab button.
     */
    activate: async (): Promise<{ message: string; onboarding_status: string }> => {
        const response = await client.post('/api/v1/onboarding/activate');
        return response.data;
    },
};

